import { useCallback, useContext, useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { LuSave } from "react-icons/lu";
import { useLoaderData, useParams } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { User, UserOrg } from "@/models/user.tsx";
import LoaderButton from "@/components/shared/LoaderButton";
import { FileSubmissionsContext } from "@/components/layout/FileSubmissionsTracker";
import { fetchAuthenticatedData } from "@/queries/shared";
import { GlobalStateModel } from "@/models";

const CAPSDAC_DOMAIN = "@capsdac.org";

type BackendFailure = { success: false; message: string | null };

function isBackendFailure(value: unknown): value is BackendFailure {
  return (
    !!value &&
    typeof value === "object" &&
    "success" in value &&
    (value as { success: unknown }).success === false
  );
}

export function UserProfileForm({
  user,
  onUpdate = () => {},
  hideActions,
  onSaveSuccess,
  onRegisterSubmit,
  onDirtyChange,
  onSubmittingChange,
}: {
  user: User<UserOrg>;
  onUpdate?: (updatedUser: User<UserOrg>) => void;
  hideActions?: boolean;
  onSaveSuccess?: (userName: string) => void;
  onRegisterSubmit?: (submitFn: () => void) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onSubmittingChange?: (submitting: boolean) => void;
}) {
  const { showToast } = useContext(FileSubmissionsContext);
  const globalState = useLoaderData({ from: "__root__" }) as GlobalStateModel;
  const queryClient = useQueryClient();
  const params = useParams({ strict: false });
  const leaid = params.leaid;
  const userid = params.userid;

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<User<UserOrg>>();

  useEffect(() => {
    reset(user);
  }, [user, reset]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const mutation = useMutation({
    mutationFn: async (updatedUser: User<UserOrg>) => {
      if (!leaid) {
        throw new Error("Missing leaid");
      }
      const saveUrl = `/json/account/${leaid}/users/${updatedUser.userName}`;
      const response = await fetchAuthenticatedData<
        User<UserOrg> | BackendFailure
      >(globalState, saveUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedUser),
      });

      if (isBackendFailure(response)) {
        throw new Error(
          response.message ||
            "Server rejected the request without an error message."
        );
      }

      return response;
    },
    onSuccess: (updatedUser) => {
      onUpdate(updatedUser);
      queryClient.setQueryData(
        ["users", leaid],
        (prev: User<UserOrg>[] | undefined) => {
          if (!prev) return [updatedUser];
          const exists = prev.some((u) => u.userName === updatedUser.userName);
          return exists
            ? prev.map((u) =>
                u.userName === updatedUser.userName ? updatedUser : u
              )
            : [...prev, updatedUser];
        }
      );
      queryClient.invalidateQueries({ queryKey: ["user", leaid, userid] });
      queryClient.invalidateQueries({ queryKey: ["identity-profile"] });
      reset(updatedUser);
      showToast("success", "Success", "Profile updated successfully.");
      if (updatedUser.userName) {
        onSaveSuccess?.(updatedUser.userName);
      }
    },
    onError: (error: Error) => {
      if (error?.message === "Missing leaid") {
        showToast("error", "Error", "Organization context is required.");
        return;
      }
      console.error("Profile update error:", error);
      showToast(
        "error",
        "Error",
        error?.message || "Failed to update profile. Please try again."
      );
    },
  });

  useEffect(() => {
    onSubmittingChange?.(mutation.isPending);
  }, [mutation.isPending, onSubmittingChange]);

  const onSubmit: SubmitHandler<User<UserOrg>> = useCallback(
    (formData) => {
      const mergedData = { ...user, ...formData };
      if (!leaid) {
        showToast("error", "Error", "Organization context is required.");
        return;
      }
      if (!mergedData.userName?.trim()) {
        showToast("error", "Error", "Username is required.");
        return;
      }
      mutation.mutate(mergedData);
    },
    [user, leaid, showToast, mutation]
  );

  useEffect(() => {
    if (onRegisterSubmit) {
      onRegisterSubmit(handleSubmit(onSubmit));
    }
  }, [onRegisterSubmit, handleSubmit, onSubmit]);

  const usernameErrorMessage = errors.userName?.message;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="p-4 pb-5">
        <div className="row g-4">
          <div className="col-12 col-md-4">
            <label className="form-label fw-semibold small" htmlFor="userName">
              Username
            </label>
            {user.userName ? (
              <div
                id="userName"
                className="form-control"
                role="textbox"
                aria-readonly="true"
                style={{
                  backgroundColor: "#f8f9fa",
                  color: "#212529",
                  cursor: "not-allowed",
                  borderRadius: "0.375rem",
                  minHeight: "38px",
                }}
              >
                {user.userName}
              </div>
            ) : (
              <>
                <input
                  id="userName"
                  className={`form-control${
                    usernameErrorMessage ? " is-invalid" : ""
                  }`}
                  type="text"
                  maxLength={100}
                  {...register("userName", {
                    validate: (value) =>
                      !value ||
                      (typeof value === "string" &&
                        value.toLowerCase().endsWith(CAPSDAC_DOMAIN))
                        ? true
                        : `Username must end with ${CAPSDAC_DOMAIN}.`,
                  })}
                />
                {usernameErrorMessage && (
                  <div
                    style={{
                      marginTop: "6px",
                      fontSize: "13px",
                      color: "#842029",
                    }}
                  >
                    {usernameErrorMessage}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label fw-semibold small" htmlFor="firstName">
              First Name
            </label>
            <input
              id="firstName"
              className="form-control"
              type="text"
              maxLength={50}
              {...register("firstName")}
            />
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label fw-semibold small" htmlFor="lastName">
              Last Name
            </label>
            <input
              id="lastName"
              className="form-control"
              type="text"
              maxLength={50}
              {...register("lastName")}
            />
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label fw-semibold small" htmlFor="email">
              Email Address
            </label>
            <input
              id="email"
              className="form-control"
              type="email"
              maxLength={50}
              {...register("email")}
            />
          </div>
          <div className="col-12 col-md-6">
            <label
              className="form-label fw-semibold small"
              htmlFor="phoneNumber"
            >
              Phone Number
            </label>
            <input
              id="phoneNumber"
              className="form-control"
              type="text"
              maxLength={15}
              {...register("phoneNumber")}
            />
          </div>
        </div>
      </div>

      {!hideActions && (
        <div className="profile-form-actions">
          <div
            className="d-flex align-items-center justify-content-between px-4 py-3"
            style={{ borderTop: "1px solid #e5e7eb" }}
          >
            <span
              style={{
                fontSize: "0.9rem",
                fontWeight: isDirty ? 600 : 400,
                color: isDirty ? "#dc3545" : "#6c757d",
              }}
            >
              {isDirty ? "Unsaved changes" : "No changes made"}
            </span>
            <LoaderButton
              type="submit"
              disabled={!isDirty}
              loading={mutation.isPending}
              loadingText="Updating..."
              className="d-flex align-items-center gap-2"
              style={{
                background: isDirty ? "var(--color-p3)" : "#e9ecef",
                border: "none",
                color: isDirty ? "#fff" : "#6c757d",
              }}
            >
              <LuSave size={15} />
              Update Profile
            </LoaderButton>
          </div>
        </div>
      )}
    </form>
  );
}
